import {
    Box,
    Card,
    Close,
    Flex,
    Grid,
    Heading,
    Image,
    Spinner,
    Text
} from '@theme-ui/components'
import { useTranslation } from 'react-i18next'
import { toast } from 'utils/toast'
import { setAdvisers } from "features/auth/authSlice"
import { useDispatch } from 'react-redux'
import {
    Button,
    Circle,
    ImagePlaceholder,
    Tooltip
} from '~/Common'
import { confirmAlert } from 'react-confirm-alert'
import { ThemeProvider } from 'theme-ui'
import { theme } from 'app/theme'
import { unlinkAdviser } from 'features/advisers/advisersAPI'
import { CSSTransition } from 'react-transition-group'
import { useState, useRef } from 'react'
import { Icon } from 'assets/Icon'

export const List = ({ advisers }) => {
    const { t } = useTranslation();
    const nodeRef = useRef(null);
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [currentAdvisers, setCurrentAdvisers] = useState(advisers ?? []);

    const handleUnlinkClick = (e, uuid) => {
        e.preventDefault();

        confirmAlert({
            customUI: ({ onClose }) => {
                return (
                    <ThemeProvider theme={theme}>
                        <Box
                            bg="white"
                            px={5}
                            py={4}
                            sx={{
                                maxWidth: '600px',
                                width: '100%',
                                textAlign: 'center',
                                boxShadow: '0 20px 75px rgb(0 0 0 / 23%)',
                                position: 'relative',
                            }}
                        >
                            <Close
                                onClick={onClose}
                                sx={{
                                    position: 'absolute',
                                    top: '10px',
                                    right: '10px',
                                }}
                            ></Close>

                            <Heading as="h2" variant="h2" mb={3}>
                                {t('Are you sure?')}
                            </Heading>
                            <Text as="p" mb={3}>
                                {t('Are you sure to want to unlink this adviser?')}
                            </Text>
                            <Text as="p" mb={3}>
                                {t("You can always re-invite them if they require access again.")}
                            </Text>
                            <Button
                                id="confirmUnlink"
                                variant="primary"
                                mr={3}
                                onClick={() => {
                                    confirmUnlink(uuid)
                                    onClose();
                                }}
                            >
                                {t('Yes')}
                            </Button>
                            <Button variant="link" onClick={onClose}>
                                {t('Cancel')}
                            </Button>
                        </Box>
                    </ThemeProvider>
                );
            },
        });
    };

    const confirmUnlink = (uuid) => {
        setLoading(true);
        unlinkAdviser(uuid).then((response) => {
            toast.success(response?.message ?? t('advisers.unlink.success'), {
                toastId: 'unlink-success',
            });

            dispatch(setAdvisers(response));
            setCurrentAdvisers(response?.data);

        }).catch(({ response }) => {
            toast.error(response?.data?.message ?? t('advisers.unlink.error'), {
                toastId: 'unlink-error',
            });
        }).finally(() => setLoading(false))
    };

    return <>
        {loading ? <Spinner variant="styles.spinner" m={'auto'} /> : (
            <CSSTransition
                nodeRef={nodeRef}
                in={true}
                timeout={500}
                classNames="fade"
                unmountOnExit
                appear
            >
                <Box ref={nodeRef} id="currentAdvisers">
                    {currentAdvisers !== undefined && currentAdvisers.length ? (
                        <>
                            {currentAdvisers.map((adviser, key) => (
                                <Card
                                    key={`adviser${key}`}
                                    variant="list"
                                    as={Box}
                                    mb={3}
                                >
                                    <Grid px={3} py={3} sx={{
                                        position: 'relative',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        gridTemplate: ['1fr', '1fr', 'auto / 50px auto 170px'],
                                        gridGap: '24px',
                                    }}>
                                        <Flex sx={{
                                            width: '80px',
                                            alignItems: 'center',
                                        }}>
                                            <Circle size="39px" bg="#f4f4f4" type="box" sx={{ flexShrink: 0, overflow: 'hidden', mr: 2 }}>
                                                {adviser?.profile_photo_url ? (
                                                    <Image src={adviser?.profile_photo_url} sx={{ borderRadius: '50%' }} />
                                                ) : (
                                                    <ImagePlaceholder imageSx={{ borderRadius: '50%' }} sx={{
                                                        width: '70px',
                                                        height: '70px',
                                                        borderRadius: '50%'
                                                    }} />
                                                )}
                                            </Circle>
                                        </Flex>

                                        <Box pb={[3, 3, 0]} sx={{
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: ['calc(100% - 80px)', 'auto'],
                                            maxWidth: '300px',
                                        }}>
                                            <Flex sx={{
                                                alignItems: 'center',
                                            }}>
                                                <Heading as="h3" variant="deal_heading_list" mb={0}>
                                                    {t(`${adviser.first_name} ${adviser.last_name}`)}
                                                </Heading>

                                                {adviser?.active ? (
                                                    <Tooltip text="Accepted" sx={{ ml: 2, height: '20px' }}>
                                                        <Icon icon="verified" size="18" />
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip text="Requires acceptance" sx={{ ml: 2, height: '20px' }}>
                                                        <Icon size="18" icon="pending" color="warning" />
                                                    </Tooltip>
                                                )}
                                            </Flex>
                                            <Text as="p" variant="tag" color="dark">{adviser?.email}</Text>
                                        </Box>

                                        <Flex p={[null, null, 2]} sx={{
                                            width: '100%',
                                            marginLeft: 'auto',
                                            flexDirection: 'column',
                                            justifyContent: 'flex-start',
                                        }}>
                                            <Button variant="bordered" className="unlinkUser" onClick={(e) => handleUnlinkClick(e, adviser.uuid)}>{t(`Unlink account`)}</Button>
                                        </Flex>
                                    </Grid>
                                </Card>
                            ))}
                        </>
                    ) : null}
                </Box>
            </CSSTransition>
        )}
    </>;
};
